/**
 * IndoorRouteBridge.ts
 *
 * IAM-M16B-R1.6R3 — Minimum Mapbox → Routing Runtime Bridge
 *
 * Connects REAL Mapbox Indoor v3 browser-runtime data to the
 * certified Wingman indoor discovery/topology/routing pipeline.
 *
 * Architecture boundary:
 *   Mapbox GL JS (browser) → IndoorV3QueryAdapter → this bridge
 *   → provider-neutral TopologyNode[] → IndoorTopologyForensics
 *   → TopologyGraph → IndoorRouteEngine → RouteResult
 *
 * Mapbox types do NOT leak past normalization.
 * No fixture fallback in the production path.
 * Fail-closed for every runtime state.
 */

import type mapboxgl from 'mapbox-gl';
import {
  indoorV3QueryAdapter,
  type IndoorV3QueryResult,
  type IndoorV3LayerName,
} from './IndoorV3QueryAdapter';
import {
  TopologyNodeType,
  PRODUCTION_DISTANCE_POLICY,
  type TopologyNode,
  type TopologyGraph,
  type DistancePolicy,
} from './IndoorTopologyContracts';
import { IndoorTopologyForensics, haversineMeters } from './IndoorTopologyForensics';
import { IndoorRouteEngine } from './IndoorRouteEngine';
import type { IndoorRouteResponse } from './IndoorRouteContracts';
import type { AirportPOI } from './AirportDiscoveryContracts';
import { AirportDiscoveryService } from './AirportDiscoveryService';
import { MapboxIndoorDiscoveryAdapter } from './MapboxIndoorDiscoveryAdapter';
import { indoorRouteGeoJsonAdapter } from './IndoorRouteGeoJsonAdapter';
import { indoorRouteRenderer } from './IndoorRouteRenderer';
import type { IndoorRouteVisualization, RouteInfoState } from './IndoorRouteVisualizationContracts';

// ── Runtime Status ──────────────────────────────────────────

export type BridgeStatus =
  | 'UNINITIALIZED'
  | 'MAP_NOT_READY'
  | 'INDOOR_SOURCE_NOT_READY'
  | 'TILES_NOT_LOADED'
  | 'FACILITY_NOT_FOUND'
  | 'AMBIGUOUS_FACILITY'
  | 'DISCOVERY_DATA_NOT_READY'
  | 'TOPOLOGY_READY'
  | 'ROUTER_READY'
  | 'START_NOT_FOUND'
  | 'DESTINATION_NOT_FOUND'
  | 'NO_CONNECTED_PATH'
  | 'AMBIGUOUS_CONNECTOR'
  | 'ROUTE_FOUND'
  | 'ERROR';

// ── Provenance ──────────────────────────────────────────────

export interface FeatureProvenance {
  readonly provider: 'MAPBOX_INDOOR_V3';
  readonly sourceLayer: string;
  readonly featureId: string | number | undefined;
  readonly facilityId?: string;
  readonly structureId?: string;
  readonly floorId?: string;
  readonly classification?: string;
  readonly rawProperties: Readonly<Record<string, unknown>>;
}

// ── Normalized Indoor Dataset ───────────────────────────────

export interface NormalizedIndoorDataset {
  readonly facilityId: string | undefined;
  readonly structureId: string | undefined;
  readonly floorCount: number;
  readonly floors: ReadonlyArray<{ level: number; name: string; id?: string }>;
  readonly corridors: readonly TopologyNode[];
  readonly doors: readonly TopologyNode[];
  readonly connectors: readonly TopologyNode[];
  readonly gates: readonly TopologyNode[];
  readonly security: readonly TopologyNode[];
  readonly labels: readonly TopologyNode[];
  readonly discoveryItems: readonly AirportPOI[];
  readonly featureProvenance: ReadonlyMap<string, FeatureProvenance>;
  readonly totalFeatures: number;
  readonly timestamp: string;
}

// ── Bridge Route Result ─────────────────────────────────────

export interface BridgeRouteResult {
  readonly status: BridgeStatus;
  readonly route: IndoorRouteResponse | null;
  readonly dataset: NormalizedIndoorDataset | null;
  readonly graph: TopologyGraph | null;
  readonly fixtureUsed: false;
  readonly error?: string;
}

export interface BridgeRouteAndVisualizeResult extends BridgeRouteResult {
  readonly visualization: IndoorRouteVisualization | null;
}

// ── The Bridge ──────────────────────────────────────────────

export class IndoorRouteBridge {
  private readonly forensics = new IndoorTopologyForensics();
  private readonly engine = new IndoorRouteEngine();
  private cachedDataset: NormalizedIndoorDataset | null = null;
  private cachedGraph: TopologyGraph | null = null;

  /**
   * Load real Mapbox Indoor v3 data from currently loaded tiles.
   * Returns a provider-neutral normalized dataset.
   * FAIL-CLOSED: returns null with status if data cannot be obtained.
   */
  loadRealData(map: mapboxgl.Map): { status: BridgeStatus; dataset: NormalizedIndoorDataset | null } {
    if (!map) return { status: 'MAP_NOT_READY', dataset: null };

    // 1. Verify indoor source is ready
    if (!indoorV3QueryAdapter.isSourceReady(map)) {
      // Try to register it
      const ok = indoorV3QueryAdapter.ensureSource(map);
      if (!ok) return { status: 'INDOOR_SOURCE_NOT_READY', dataset: null };
    }

    // 2. Query all relevant layers
    const queries = {
      facilityMeta: this.safeQuery(map, 'indoor_facility_metadata'),
      structureMeta: this.safeQuery(map, 'indoor_structure_metadata'),
      floorMeta: this.safeQuery(map, 'indoor_floor_metadata'),
      floorplan: this.safeQuery(map, 'indoor_floorplan'),
      doors: this.safeQuery(map, 'indoor_door'),
      structures: this.safeQuery(map, 'indoor_structure'),
      labels: this.safeQuery(map, 'indoor_label'),
    };

    if (Object.values(queries).some(result =>
      result.status === 'SOURCE_NOT_READY' || result.status === 'LAYER_NOT_AVAILABLE')) {
      return { status: 'DISCOVERY_DATA_NOT_READY', dataset: null };
    }

    const { facilityMeta, structureMeta } = queries;

    // 3. Check if we got any features at all
    const totalFeatures =
      facilityMeta.features.length + structureMeta.features.length +
      queries.floorMeta.features.length + queries.floorplan.features.length +
      queries.doors.features.length + queries.structures.features.length +
      queries.labels.features.length;

    if (totalFeatures === 0) {
      return { status: 'TILES_NOT_LOADED', dataset: null };
    }

    // 4. Extract facility/structure IDs
    const facilityIds = new Set(facilityMeta.features
      .map(feature => feature.properties?.id ?? feature.id)
      .filter(id => id != null)
      .map(String));
    if (facilityIds.size > 1) {
      return { status: 'AMBIGUOUS_FACILITY', dataset: null };
    }
    const facilityId = facilityIds.values().next().value;
    const structureId = structureMeta.features[0]?.properties?.id ??
                        structureMeta.features[0]?.id;

    if (facilityId == null) {
      return { status: 'FACILITY_NOT_FOUND', dataset: null };
    }

    const scoped = {
      floors: this.scopeFeatures(queries.floorMeta.features, facilityId, structureId),
      floorplan: this.scopeFeatures(queries.floorplan.features, facilityId, structureId),
      doors: this.scopeFeatures(queries.doors.features, facilityId, structureId),
      structures: this.scopeFeatures(queries.structures.features, facilityId, structureId),
      labels: this.scopeFeatures(queries.labels.features, facilityId, structureId),
    };

    // 5. Normalize floors
    const floors = this.normalizeFloors(scoped.floors);

    // 6. Normalize topology nodes from real features
    const provenance = new Map<string, FeatureProvenance>();
    const corridorNodes = this.normalizeFloorplanToCorridors(scoped.floorplan, provenance);
    const doorNodes = this.normalizeDoors(scoped.doors, provenance);
    const connectorNodes = this.normalizeConnectors(scoped.structures, provenance);
    const { gates, security, allLabels } = this.normalizeLabels(scoped.labels, provenance);
    const discoveryItems = this.normalizeDiscoveryItems(scoped.labels);

    const dataset: NormalizedIndoorDataset = {
      facilityId: facilityId != null ? String(facilityId) : undefined,
      structureId: structureId != null ? String(structureId) : undefined,
      floorCount: floors.length,
      floors,
      corridors: corridorNodes,
      doors: doorNodes,
      connectors: connectorNodes,
      gates,
      security,
      labels: allLabels,
      discoveryItems,
      featureProvenance: provenance,
      totalFeatures,
      timestamp: new Date().toISOString(),
    };

    this.cachedDataset = dataset;
    return { status: 'TOPOLOGY_READY', dataset };
  }

  /**
   * Build routing graph from the loaded real dataset.
   */
  buildGraph(dataset: NormalizedIndoorDataset, policy?: DistancePolicy): TopologyGraph {
    const p = policy ?? PRODUCTION_DISTANCE_POLICY;
    // Labels already contain the gate and security subsets; insert every node once.
    const allCorridors = [...dataset.corridors, ...dataset.labels];
    const graph = this.forensics.buildGraph(allCorridors, [...dataset.doors], [...dataset.connectors], p);
    this.cachedGraph = graph;
    return graph;
  }

  /**
   * Solve a route from origin to destination using real data.
   * FAIL-CLOSED: returns error status if any step cannot be proven.
   */
  solveRoute(
    map: mapboxgl.Map,
    originQuery: string,
    destinationQuery: string,
  ): BridgeRouteResult {
    // 1. Load real data
    const { status: loadStatus, dataset } = this.loadRealData(map);
    if (!dataset) {
      return { status: loadStatus, route: null, dataset: null, graph: null, fixtureUsed: false, error: `Data load failed: ${loadStatus}` };
    }

    // 2. Build graph
    const graph = this.buildGraph(dataset);

    // 3. Resolve endpoints through the provider-neutral discovery pipeline.
    const discovery = new AirportDiscoveryService();
    discovery.index(dataset.discoveryItems);

    const destinationMatch = discovery.search({ query: destinationQuery, limit: 1 }).results[0];
    if (!destinationMatch || destinationMatch.score < 0.6) {
      return { status: 'DESTINATION_NOT_FOUND', route: null, dataset, graph, fixtureUsed: false, error: 'Destination not found' };
    }

    // Prioritize destination terminal when resolving origin to prevent cross-terminal ambiguity
    const originMatch = (destinationMatch.poi.terminal
      ? discovery.search({ query: originQuery, terminal: destinationMatch.poi.terminal, limit: 1 }).results[0]
      : undefined) ?? discovery.search({ query: originQuery, limit: 1 }).results[0];

    if (!originMatch || originMatch.score < 0.6) {
      return { status: 'START_NOT_FOUND', route: null, dataset, graph, fixtureUsed: false, error: 'Origin not found' };
    }

    // Map discovery POIs to corresponding topology graph nodes by coordinates
    const findNodeForPoi = (poi: AirportPOI): TopologyNode | undefined => {
      let bestNode: TopologyNode | undefined;
      let minDistance = Infinity;
      for (const [, node] of graph.nodes) {
        if (poi.level !== undefined && poi.level !== null && node.level !== poi.level) continue;
        const dist = haversineMeters(
          [node.coordinates[0], node.coordinates[1]],
          [poi.coordinates[0], poi.coordinates[1]]
        );
        if (dist < minDistance) {
          minDistance = dist;
          bestNode = node;
        }
      }
      return minDistance <= 35 ? bestNode : undefined;
    };

    const originNode = findNodeForPoi(originMatch.poi);
    const destNode = findNodeForPoi(destinationMatch.poi);

    const originId = originNode ? originNode.id : originMatch.poi.name;
    const destId = destNode ? destNode.id : destinationMatch.poi.name;

    const route = this.engine.solve(graph, originId, destId);

    if (!route.success) {
      // Determine specific failure status
      const errorMsg = route.error ?? '';
      let failStatus: BridgeStatus = 'NO_CONNECTED_PATH';
      if (errorMsg.includes('Origin not found')) failStatus = 'START_NOT_FOUND';
      if (errorMsg.includes('Destination not found')) failStatus = 'DESTINATION_NOT_FOUND';
      return { status: failStatus, route: null, dataset, graph, fixtureUsed: false, error: route.error };
    }

    return { status: 'ROUTE_FOUND', route, dataset, graph, fixtureUsed: false };
  }

  /**
   * Solve a route from origin to destination and render it onto the map.
   * If route calculation fails or destination is not found, existing visualization is cleared.
   */
  routeAndVisualize(
    map: mapboxgl.Map,
    originQuery: string,
    destinationQuery: string,
    activeFloor?: number
  ): BridgeRouteAndVisualizeResult {
    const result = this.solveRoute(map, originQuery, destinationQuery);
    if (!result.route || !result.graph) {
      indoorRouteRenderer.clearRoute(map);
      return { ...result, visualization: null };
    }

    const coordsMap = new Map<string, [number, number]>();
    const levelsMap = new Map<string, number>();
    for (const [id, node] of result.graph.nodes) {
      coordsMap.set(id, [node.coordinates[0], node.coordinates[1]]);
      levelsMap.set(id, node.level);
    }

    const viz = indoorRouteGeoJsonAdapter.toVisualization(result.route, coordsMap, levelsMap);
    if (!viz) {
      indoorRouteRenderer.clearRoute(map);
      return { ...result, visualization: null };
    }

    indoorRouteRenderer.renderRoute(map, viz, activeFloor);
    return { ...result, visualization: viz };
  }

  /**
   * Clears the current indoor route visualization from the map.
   */
  clearVisualization(map: mapboxgl.Map): void {
    indoorRouteRenderer.clearRoute(map);
  }

  // ── Private normalization helpers ───────────────────────────

  private safeQuery(map: mapboxgl.Map, layer: IndoorV3LayerName): IndoorV3QueryResult {
    try {
      return indoorV3QueryAdapter.queryLayer(map, layer);
    } catch {
      return { layer, isAllowlisted: true, status: 'SOURCE_NOT_READY' as any, features: [] };
    }
  }

  private scopeFeatures(features: any[], facilityId: unknown, structureId: unknown): any[] {
    const targetFacility = String(facilityId);
    const targetStructure = structureId == null ? undefined : String(structureId);
    return features.filter(feature => {
      const properties = feature.properties ?? {};
      const featureFacility = properties.facility_id ?? properties.facilityId;
      const featureStructure = properties.structure_id ?? properties.structureId;
      if (featureFacility != null && String(featureFacility) !== targetFacility) return false;
      if (targetStructure && featureStructure != null && String(featureStructure) !== targetStructure) return false;
      return true;
    });
  }

  private normalizeDiscoveryItems(features: any[]): readonly AirportPOI[] {
    const adapter = new MapboxIndoorDiscoveryAdapter();
    const pois = features
      .map(feature => adapter.normalize({
        ...(feature.properties ?? {}),
        geometry: feature.geometry,
        sourceLayer: feature.sourceLayer ?? 'indoor_label',
      }))
      .filter((poi): poi is AirportPOI => poi !== null);
    return adapter.deduplicate(pois);
  }

  private normalizeFloors(features: any[]): Array<{ level: number; name: string; id?: string }> {
    const seen = new Map<number, { level: number; name: string; id?: string }>();
    for (const f of features) {
      const props = f.properties ?? {};
      const level = props.level ?? props.ordinal ?? 0;
      const name = props.name ?? props.short_name ?? `Level ${level}`;
      const id = props.id ?? f.id;
      if (!seen.has(level)) {
        seen.set(level, { level: Number(level), name: String(name), id: id != null ? String(id) : undefined });
      }
    }
    return [...seen.values()].sort((a, b) => a.level - b.level);
  }

  private normalizeFloorplanToCorridors(features: any[], provenance: Map<string, FeatureProvenance>): TopologyNode[] {
    const nodes: TopologyNode[] = [];
    for (const f of features) {
      const props = f.properties ?? {};
      const coords = this.extractCentroid(f.geometry);
      if (!coords) continue;

      const level = Number(props.level ?? props.ordinal ?? 0);
      const cat = String(props.category ?? props.type ?? 'room').toLowerCase();
      const isWalkable = cat.includes('corridor') || cat.includes('hall') || cat.includes('walkway') || cat.includes('room') || cat.includes('area');
      if (!isWalkable && !cat.includes('lobby') && !cat.includes('concourse')) continue;

      const id = `fp-${nodes.length}-L${level}`;
      const node: TopologyNode = {
        id,
        type: TopologyNodeType.CORRIDOR,
        coordinates: coords,
        level,
        label: props.name ?? props.category ?? 'Corridor',
      };
      nodes.push(node);
      provenance.set(id, this.buildProvenance(f, 'indoor_floorplan'));
    }
    return nodes;
  }

  private normalizeDoors(features: any[], provenance: Map<string, FeatureProvenance>): TopologyNode[] {
    const nodes: TopologyNode[] = [];
    for (const f of features) {
      const coords = this.extractCentroid(f.geometry);
      if (!coords) continue;
      const props = f.properties ?? {};
      const level = Number(props.level ?? props.ordinal ?? 0);
      const id = `door-${nodes.length}-L${level}`;
      const node: TopologyNode = {
        id,
        type: TopologyNodeType.DOOR,
        coordinates: coords,
        level,
        label: props.name ?? 'Door',
      };
      nodes.push(node);
      provenance.set(id, this.buildProvenance(f, 'indoor_door'));
    }
    return nodes;
  }

  private normalizeConnectors(features: any[], provenance: Map<string, FeatureProvenance>): TopologyNode[] {
    const nodes: TopologyNode[] = [];
    for (const f of features) {
      const props = f.properties ?? {};
      const cat = String(props.category ?? props.type ?? '').toLowerCase();
      const isConnector = cat.includes('elevator') || cat.includes('escalator') ||
                          cat.includes('stair') || cat.includes('ramp');
      if (!isConnector) continue;

      const coords = this.extractCentroid(f.geometry);
      if (!coords) continue;
      const level = Number(props.level ?? props.ordinal ?? 0);

      // Derive connected floors from metadata
      const connectedFloors: number[] = [];
      if (props.connected_levels) {
        try {
          const parsed = typeof props.connected_levels === 'string'
            ? JSON.parse(props.connected_levels) : props.connected_levels;
          if (Array.isArray(parsed)) connectedFloors.push(...parsed.map(Number));
        } catch { /* ignore */ }
      }
      if (connectedFloors.length === 0 && props.from_level != null && props.to_level != null) {
        connectedFloors.push(Number(props.from_level), Number(props.to_level));
      }
      // Cross-floor topology must be provider-proven; never invent adjacent floors.
      if (connectedFloors.length === 0) continue;

      const id = `connector-${cat}-${nodes.length}-L${level}`;
      const node: TopologyNode = {
        id,
        type: TopologyNodeType.CONNECTOR,
        coordinates: coords,
        level,
        label: props.name ?? cat,
        connectedFloorIds: connectedFloors,
      };
      nodes.push(node);
      provenance.set(id, this.buildProvenance(f, 'indoor_structure'));
    }
    return nodes;
  }

  private normalizeLabels(features: any[], provenance: Map<string, FeatureProvenance>): {
    gates: TopologyNode[];
    security: TopologyNode[];
    allLabels: TopologyNode[];
  } {
    const gates: TopologyNode[] = [];
    const security: TopologyNode[] = [];
    const allLabels: TopologyNode[] = [];

    for (const f of features) {
      const props = f.properties ?? {};
      const coords = this.extractCentroid(f.geometry);
      if (!coords) continue;
      const level = Number(props.level ?? props.ordinal ?? 0);
      const name = String(props.name ?? props.label ?? '');
      const cat = String(props.category ?? props.type ?? '').toLowerCase();

      const isGate = cat.includes('gate') || /^[A-Z]?\d+$/.test(name) || /gate/i.test(name);
      const isSecurity = cat.includes('security') || /tsa|security|checkpoint/i.test(name);

      let type = TopologyNodeType.CORRIDOR;
      if (isGate) type = TopologyNodeType.GATE;
      if (isSecurity) type = TopologyNodeType.SECURITY;

      const id = `label-${allLabels.length}-L${level}`;
      const node: TopologyNode = {
        id,
        type,
        coordinates: coords,
        level,
        label: name || cat || 'Label',
      };

      if (isGate) gates.push(node);
      if (isSecurity) security.push(node);
      allLabels.push(node);
      provenance.set(id, this.buildProvenance(f, 'indoor_label'));
    }

    return { gates, security, allLabels };
  }

  private extractCentroid(geometry: any): readonly [number, number] | null {
    if (!geometry) return null;
    const type = geometry.type;
    const coords = geometry.coordinates;
    if (!coords) return null;

    if (type === 'Point') return [coords[0], coords[1]] as const;
    if (type === 'MultiPoint' && coords.length > 0) return [coords[0][0], coords[0][1]] as const;

    // For polygons, compute centroid
    if (type === 'Polygon' && coords.length > 0) {
      return this.computeCentroid(coords[0]);
    }
    if (type === 'MultiPolygon' && coords.length > 0 && coords[0].length > 0) {
      return this.computeCentroid(coords[0][0]);
    }
    if (type === 'LineString' && coords.length > 0) {
      const mid = Math.floor(coords.length / 2);
      return [coords[mid][0], coords[mid][1]] as const;
    }

    return null;
  }

  private computeCentroid(ring: number[][]): readonly [number, number] | null {
    if (!ring || ring.length === 0) return null;
    let sumLng = 0, sumLat = 0;
    for (const [lng, lat] of ring) { sumLng += lng; sumLat += lat; }
    return [sumLng / ring.length, sumLat / ring.length] as const;
  }

  private buildProvenance(feature: any, sourceLayer: string): FeatureProvenance {
    return {
      provider: 'MAPBOX_INDOOR_V3',
      sourceLayer,
      featureId: feature.id,
      facilityId: feature.properties?.facility_id,
      structureId: feature.properties?.structure_id,
      floorId: feature.properties?.floor_id,
      classification: feature.properties?.category ?? feature.properties?.type,
      rawProperties: { ...feature.properties },
    };
  }
}

// ── Singleton ───────────────────────────────────────────────

export const indoorRouteBridge = new IndoorRouteBridge();

// ── Development diagnostic surface ──────────────────────────
// Follows existing Wingman pattern (WingmanIndoor, WingmanIndoorV3)
// No secrets. No raw tokens. Development only.

if (typeof window !== 'undefined') {
  (window as any).WingmanIndoorRoute = {
    /**
     * Load real Mapbox Indoor v3 data from currently loaded tiles.
     * Usage: WingmanIndoorRoute.loadData(map)
     */
    loadData: (map: mapboxgl.Map) => indoorRouteBridge.loadRealData(map),

    /**
     * Solve a route from origin to destination using real Mapbox data.
     * Usage: WingmanIndoorRoute.route('TSA Security', 'Gate B20')
     *    or: WingmanIndoorRoute.route(map, 'TSA Security', 'Gate B20')
     */
    route: (arg1?: any, arg2?: string, arg3?: string) => {
      let targetMap: any;
      let origin: string;
      let dest: string;
      if (typeof arg1 === 'string') {
        targetMap = (window as any).map;
        origin = arg1;
        dest = arg2 || '';
      } else {
        targetMap = arg1 || (window as any).map;
        origin = arg2 || '';
        dest = arg3 || '';
      }
      return indoorRouteBridge.solveRoute(targetMap, origin, dest);
    },

    /**
     * Solve and render route on Mapbox map.
     * Usage: WingmanIndoorRoute.routeAndVisualize('TSA Security', 'Gate B20')
     *    or: WingmanIndoorRoute.routeAndVisualize(map, 'TSA Security', 'Gate B20')
     */
    routeAndVisualize: (
      arg1?: any,
      arg2?: string,
      arg3?: string | number,
      arg4?: number
    ) => {
      let targetMap: any;
      let origin: string;
      let dest: string;
      let activeFloor: number | undefined;

      if (typeof arg1 === 'string') {
        targetMap = (window as any).map;
        origin = arg1;
        dest = arg2 || '';
        activeFloor = typeof arg3 === 'number' ? arg3 : undefined;
      } else {
        targetMap = arg1 || (window as any).map;
        origin = arg2 || '';
        dest = (typeof arg3 === 'string' ? arg3 : '') || '';
        activeFloor = arg4;
      }
      return indoorRouteBridge.routeAndVisualize(targetMap, origin, dest, activeFloor);
    },

    /**
     * Clear the rendered route from the map.
     * Usage: WingmanIndoorRoute.clearVisualization()
     */
    clearVisualization: (map?: mapboxgl.Map) => {
      const targetMap = map || (window as any).map;
      return indoorRouteBridge.clearVisualization(targetMap);
    },

    /**
     * Get active route visualization object or null.
     */
    getActiveRoute: () => indoorRouteRenderer.getActiveRoute(),

    /**
     * Get lightweight route info summary.
     */
    getRouteInfo: () => indoorRouteRenderer.getRouteInfo(),

    /**
     * Set floor filter on route layers.
     */
    setFloor: (map: mapboxgl.Map, floorLevel: number) =>
      indoorRouteRenderer.setFloor(map, floorLevel),

    renderer: indoorRouteRenderer,
    adapter: indoorRouteGeoJsonAdapter,

    /**
     * Expose the certified production policy (read-only)
     */
    policy: PRODUCTION_DISTANCE_POLICY,
  };
}
